import { useCallback } from 'react';
import type { NoteColor } from '@/enums/noteColor';
import { NOTE_PALETTE } from '@/constants/notes';
import { useNotesState } from '@/hooks/useNotes';
import { useNotesDispatch } from '@/hooks/useNotesDispatch';
import { redo, undo } from '@/state/notesActions';
import { SyncStatusBadge } from '@/components/SyncStatusBadge';
import styles from './Toolbar.module.css';

interface Props {
  readonly selectedColor: NoteColor;
  readonly onColorSelect: (color: NoteColor) => void;
}

export function Toolbar({ selectedColor, onColorSelect }: Props) {
  const state = useNotesState();
  const dispatch = useNotesDispatch();

  const canUndo = state.past.length > 0;
  const canRedo = state.future.length > 0;
  const count = state.order.length;

  const handleUndo = useCallback(() => dispatch(undo()), [dispatch]);
  const handleRedo = useCallback(() => dispatch(redo()), [dispatch]);

  return (
    <header className={styles.toolbar}>
      <div className={styles.brand}>
        <span className={styles.brandMark} aria-hidden>
          <svg viewBox="0 0 20 20" width="18" height="18">
            <rect x="2" y="2" width="16" height="16" rx="1.5" fill="#ffd966" stroke="#b58f2e" />
            <path
              d="M6 8h8M6 11h8M6 14h5"
              stroke="#b58f2e"
              strokeWidth="1.2"
              strokeLinecap="round"
            />
          </svg>
        </span>
        <span className={styles.brandText}>Sticky Notes</span>
        <span className={styles.count} aria-label={`${count} notes`}>
          {count}
        </span>
      </div>

      <div className={styles.controls}>
        <div className={styles.colorGroup} role="radiogroup" aria-label="New note color">
          {NOTE_PALETTE.map((color) => (
            <button
              key={color}
              type="button"
              className={`${styles.colorSwatch} ${styles[`color_${color}`]} ${
                color === selectedColor ? styles.colorSwatchActive : ''
              }`}
              role="radio"
              aria-checked={color === selectedColor}
              aria-label={`Use ${color}`}
              onClick={() => onColorSelect(color)}
            />
          ))}
        </div>

        <div className={styles.divider} aria-hidden />

        <button
          type="button"
          className={styles.iconButton}
          onClick={handleUndo}
          disabled={!canUndo}
          aria-label="Undo"
          title="Undo (⌘Z)"
        >
          <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden>
            <path
              d="M7 4L3 8l4 4M3 8h7a3 3 0 0 1 0 6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
        <button
          type="button"
          className={styles.iconButton}
          onClick={handleRedo}
          disabled={!canRedo}
          aria-label="Redo"
          title="Redo (⌘⇧Z)"
        >
          <svg viewBox="0 0 16 16" width="16" height="16" aria-hidden>
            <path
              d="M9 4l4 4-4 4M13 8H6a3 3 0 0 0 0 6"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.6"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>

        <div className={styles.divider} aria-hidden />

        <SyncStatusBadge />
      </div>
    </header>
  );
}
